// #########################################################################
//
// This service is used to keep track of all information about the
// canvas as well as all information related to it
//
// #########################################################################

import { TurtleService } from '../services/turtle.service';
import { CanvasComponent } from '../components/canvas.component';
import { ColorService } from '../services/color.service';
import { Rectangle } from '../lib/Rectangle';
import { Utils } from '../lib/Utils';
import { SVGImage } from '../lib/SVGImage';

export class CanvasService {

  /*
    Can be used to enforce a minimal stroke-width that is larger
    than 1. This value is added onto the pen width specified by
    the user. To the user, this is not visible.
  */
  private readonly PEN_WIDTH_ADDEND: number = 1;

  // Factor between the viewable canvas and the actual dimension
  private readonly BACKGROUND_SIZE_FACTOR: number = 10;

  // Margin that is added to the drawing when downloaded
  private readonly PRINT_MARGIN: number = 10;

  // Attributes for SVG drawing
  private readonly SVG_ATTRIBUTES: string = 'stroke-linecap="square" shape-rendering="crispEdges"';

  // Rendering contexts of turtle and drawing canvas'
  public contextTurtle: CanvasRenderingContext2D;
  public contextDraw: CanvasRenderingContext2D;
  public contextBackground: CanvasRenderingContext2D;

  // Information about image and canvas dimension
  private canvasWidth;
  private canvasHeight;
  private imageWidth;
  private imageHeight;

  // Canvas image stored as vector and bitmap
  private SVGDrawing: SVGImage;
  private SVGBackground: SVGImage;
  private bitmapDrawing: HTMLImageElement;
  private bitmapBackground: HTMLImageElement;
  private completeRedraw: boolean;

  // Information about 
  private viewport: Rectangle;
  private screenColor: ColorService;

  // An array of [x, y, direction, visibility] tuples, signifying the turtles' positions, directions and visitbility at the point in time the last canvas flush was executed.
  // (currently, the canvas has only one turtle, but as soon as Nils' changes are merged, we can have multiple ones on the canvas)
  private turtleInfoAtLastFlush: any[][] = new Array();

  constructor() {
    this.screenColor = new ColorService("white");
    this.bitmapDrawing = new Image();
    this.bitmapBackground = new Image();

    /*
      Even though everything is done programatically, we need to
      wait for bitmapImage to finish loading when changing the source
      (see flushImageData())
    */
    this.bitmapDrawing.onload = function() {
      this.contextDraw.drawImage(this.bitmapDrawing, this.viewport.x, this.viewport.y, this.canvasWidth, this.canvasHeight, 0, 0, this.canvasWidth, this.canvasHeight);
    }.bind(this);
    this.bitmapBackground.onload = function() {
      this.contextBackground.drawImage(this.bitmapBackground, 0, 0);
    }.bind(this);
  }

  // Used to set image data from outside (for example by the debugger)
  public getImageData(): string[] {
    return [this.SVGBackground.getSVGString(), this.SVGDrawing.getSVGString()];
  }

  // Provides image data for external use (for example for the debugger)
  public setImageData(imageData: string[]) {
    this.SVGBackground.setSVGFromString(imageData[0]);
    this.SVGDrawing.setSVGFromString(imageData[1]);
    this.completeRedraw = true;
  }

  // Creates a new canvas
  public initializeCanvas(canvasWidth: number, canvasHeight: number): void {
    this.canvasHeight = canvasHeight;
    this.canvasWidth = canvasWidth;
    this.imageHeight = this.BACKGROUND_SIZE_FACTOR * canvasHeight;
    this.imageWidth = this.BACKGROUND_SIZE_FACTOR * canvasWidth;

    // Create empty canvas with centered viewport
    this.clearScreen();
  }

  public setCanvasDimension(canvasWidth: number, canvasHeight: number) {
    // Calculate offsets for translation
    let dx = - (canvasWidth - this.canvasWidth) / 2;
    let dy = - (canvasHeight - this.canvasHeight) / 2;

    // Adjust canvas properties
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.viewport.width = canvasWidth;
    this.viewport.height = canvasHeight;

    // Translate canvas to keep up with resize if possible
    if (this.viewport.x + dx > 0 && this.viewport.getRight() + dx < this.imageWidth) {
      this.viewport.x += dx;
    } else if (this.viewport.getRight() + dx > this.imageWidth) {
      this.viewport.x += dx * 2;
    }
    if (this.viewport.y + dy > 0 && this.viewport.getBottom() + dy < this.imageHeight) {
      this.viewport.y += dy;
    } else if (this.viewport.getBottom() + dy > this.imageHeight) {
      this.viewport.y += dy * 2;
    }

    // Changing canvas dimensions clears canvas, thus a complete redraw is needed
    this.completeRedraw = true;
  }

  public getViewedRegionCenterX(): number {
    let middleX = this.viewport.x + this.viewport.width / 2;
    return middleX;
  }

  public getViewedRegionCenterY(): number {
    let middleY = this.viewport.y + this.viewport.height / 2;
    return middleY;
  }

  public dragCanvas(deltaX: number, deltaY: number): void {
    if (this.isOutsideImageData(this.viewport, deltaX, deltaY)) {
      return;
    }
    this.viewport.x += deltaX;
    this.viewport.y += deltaY;
    this.contextTurtle.clearRect(0, 0, this.viewport.width, this.viewport.height);

    // Dragging canvas changes viewport, need complete redraw
    this.completeRedraw = true;
    this.flushImageData();
  }

  private isOutsideImageData(region: Rectangle, deltaX: number, deltaY: number): boolean {
    return region.x + deltaX < 0
      || region.y + deltaY < 0
      || region.getRight() + deltaX >= this.imageWidth
      || region.getBottom() + deltaY >= this.imageHeight;
  }

  /*
    bitmapImage and backgroundImage have a handler attached
    to them that will update the canvas' once the source is
    changed (see constructor)
  */
  private flushImageData(): void {
    // Flush background if necessary
    if (this.SVGBackground.hasChanged()) {
      this.bitmapBackground.src = this.SVGBackground.getSVGURI();
    }

    // Sometimes a complete drawing is needed
    if (this.completeRedraw) {
      this.contextDraw.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.bitmapDrawing.src = this.SVGDrawing.getSVGURI();
      this.completeRedraw = false;
    }
    
    // If possible only draw incrementally over previous stuff
    if (this.SVGDrawing.hasChanged()) {
      this.bitmapDrawing.src = this.SVGDrawing.getSVGUpdateURI();
    }
  }

  public storeTurtleInfoForFlush(turtle: TurtleService): void {
    this.turtleInfoAtLastFlush[0] = new Array();
    this.turtleInfoAtLastFlush[0][0] = turtle.getX();
    this.turtleInfoAtLastFlush[0][1] = turtle.getY();
    this.turtleInfoAtLastFlush[0][2] = turtle.getDirection();
    this.turtleInfoAtLastFlush[0][3] = turtle.getVisibility();
  }

  private clearTurtleBeforeFlush(turtleId: number): void {
    let turtleInfo = this.turtleInfoAtLastFlush[turtleId];
    if (turtleInfo[3]) {
      this.contextTurtle.save();
      this.contextTurtle.translate(turtleInfo[0] - this.viewport.x, turtleInfo[1] - this.viewport.y);
      this.contextTurtle.rotate(turtleInfo[2] * Math.PI / 180);
      this.contextTurtle.clearRect(-CanvasComponent.img.width - 1, -CanvasComponent.img.height - 1, CanvasComponent.img.width * 2 + 2, CanvasComponent.img.height * 2 + 2);
      this.contextTurtle.restore();
    }
  }

  // draw turtle rotated
  public drawTurtle(turtle: TurtleService): void {
    if (turtle.getVisibility()) {
      this.contextTurtle.save();
      this.contextTurtle.translate(turtle.getX() - this.viewport.x, turtle.getY() - this.viewport.y);
      this.contextTurtle.rotate(turtle.getDirection() * Math.PI / 180);
      this.contextTurtle.drawImage(CanvasComponent.img, -CanvasComponent.img.width / 2, -CanvasComponent.img.height / 2, CanvasComponent.img.width, CanvasComponent.img.height);
      this.contextTurtle.restore();
    }
  }

  // remove turtle from canvas
  public clearTurtle(turtle: TurtleService): void {
    if (turtle.getVisibility()) {
      this.contextTurtle.save();
      this.contextTurtle.translate(turtle.getX() - this.viewport.x, turtle.getY() - this.viewport.y);
      this.contextTurtle.rotate(turtle.getDirection() * Math.PI / 180);
      this.contextTurtle.clearRect(-CanvasComponent.img.width / 2 - 1, -CanvasComponent.img.height / 2 - 1, CanvasComponent.img.width + 2, CanvasComponent.img.height + 2);
      this.contextTurtle.restore();
    }
  }

  // A turtle moves from (x1, y1) to (x2, y2) and leaves behind a stroke
  public makeTurtleStroke(x1: number, y1: number, x2: number, y2: number, color: ColorService, penWidth: number) {
    this.SVGDrawing.addLine(x1, y1, x2, y2, 'stroke="' + color.getColorString() + '" stroke-width="' + (penWidth + this.PEN_WIDTH_ADDEND) + '"');
  }

  public makeTurtleCircle(r: number, cx: number, cy: number, color: ColorService, penWidth: number): void {
    this.SVGDrawing.addCircle(cx, cy, r, 'stroke="' + color.getColorString() + '" stroke-width="' + (penWidth + this.PEN_WIDTH_ADDEND) + '" fill="none"');
  }

  public flushCanvas(turtle): void {
    this.flushImageData();
    this.clearTurtleBeforeFlush(0);
    this.storeTurtleInfoForFlush(turtle);
    this.drawTurtle(turtle);
  }

  public changeScreenColor(screenColor: ColorService): void {
    this.SVGBackground.setBackgroundColor(screenColor.getColorString());
    this.screenColor = screenColor;
  }

  //used in pe() and ppt() for erasing previous commands
  public getBackgroundColor(){
    return this.screenColor;
  }

  public clearScreen(): void {
    // Create blank, white canvas
    this.screenColor = new ColorService("white");
    this.createEmptyCanvas();

    // Recenter canvas viewport
    this.viewport = new Rectangle(this.canvasWidth, this.canvasHeight, this.canvasWidth, this.canvasHeight);
    this.viewport.x = (this.imageWidth - this.canvasWidth) / 2;
    this.viewport.y = (this.imageHeight - this.canvasHeight) / 2;

    // Redraw empty canvas and turtle
	  this.contextTurtle.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
  }

  public washCanvas() {
    this.createEmptyCanvas();
  }

  private createEmptyCanvas() {
    // Construct SVG for background
    this.SVGBackground = new SVGImage(this.imageWidth, this.imageHeight);
    this.SVGBackground.setBackgroundColor(this.screenColor.getColorString());

    // Construct SVG for the actual drawing
    this.SVGDrawing = new SVGImage(this.imageWidth, this.imageHeight, this.SVG_ATTRIBUTES);

    // Flush new canvas' to screen
    this.contextDraw.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    this.flushImageData();
  }

  // download canvas as png
  public downloadAsPNG(a: Element, name: string) {
    // Get coordinates of drawing
    let SVGSerializedNew: string = this.SVGDrawing.getSVGString();
    let xRegex: RegExp = /(x1|x2)=(?:")(.*?)(")/g;
    let yRegex: RegExp = /(y1|y2)=(?:")(.*?)(")/g;
    let xCoords: string[] = Utils.regexAllMatches(xRegex, SVGSerializedNew, 2);
    let yCoords: string[] = Utils.regexAllMatches(yRegex, SVGSerializedNew, 2);

    // Get min/max coordinates, check for out of bounds
    let minX: number = -this.PRINT_MARGIN + Math.max(Math.min.apply(null, xCoords), 0);
    let maxX: number =  this.PRINT_MARGIN + Math.min(Math.max.apply(null, xCoords), this.imageWidth);
    let minY: number = -this.PRINT_MARGIN + Math.max(Math.min.apply(null, yCoords), 0);
    let maxY: number =  this.PRINT_MARGIN + Math.min(Math.max.apply(null, yCoords), this.imageHeight);

    // Check if drawing is empty (no coordinates obtained)
    if (xCoords.length == 0) {
      minX = this.imageWidth / 2;
      maxX = this.imageWidth / 2;
      minY = this.imageHeight / 2;
      maxY = this.imageHeight / 2;
    }
    
    // Calculate drawing dimensions (including small margin)
    let clippingWidth: number = Math.min(maxX - minX, this.imageWidth);
    let clippingHeight: number = Math.min(maxY - minY, this.imageHeight);
    let x1:number = minX - Math.max(this.canvasWidth - clippingWidth, 0) / 2;
    let x2:number = maxX + Math.max(this.canvasWidth - clippingWidth, 0) / 2;
    let y1:number = minY - Math.max(this.canvasHeight - clippingHeight, 0) / 2;
    let y2:number = maxY + Math.max(this.canvasHeight - clippingHeight, 0) / 2;

    // Create new canvas and download
    let clippingCanvas = Utils.createCanvasFromImage([this.bitmapBackground, this.bitmapDrawing], x1, y1, x2 - x1, y2 - y1);
    Utils.downloadCanvas(clippingCanvas, name);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/canvas.service.ts